import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline, Box } from "@mui/material";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LoginPage from "./components/LoginPage";
import Navbar from "./components/Navbar";
import Dashboard from "./components/Dashboard";
import { useQuery } from "@tanstack/react-query";
import { createBrandTheme } from "./theme";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

const fetchNavbarColor = async () => {
  try {
    const res = await fetch(`${API_URL}/api/system/navbar-color`);
    const data = await res.json();
    return data.color || undefined;
  } catch {
    return undefined;
  }
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <>{children}</> : <Navigate to="/login" />;
};

const AppContent: React.FC = () => {
  const { user } = useAuth();
  return (
    <Router>
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
        {user && <Navbar />}
        <Box component="main" sx={{ flexGrow: 1, bgcolor: "background.default" }}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" />} />
          </Routes>
        </Box>
      </Box>
    </Router>
  );
};

const App: React.FC = () => {
  const { data: navbarColor, refetch } = useQuery({
    queryKey: ["navbarColor"],
    queryFn: fetchNavbarColor,
  });

  useEffect(() => {
    const onColorChange = () => refetch();
    window.addEventListener("navbarColorChanged", onColorChange);
    return () => window.removeEventListener("navbarColorChanged", onColorChange);
  }, [refetch]);

  const theme = createBrandTheme(navbarColor || undefined);

  try {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </ThemeProvider>
    );
  } catch (error) {
    console.error("App rendering error:", error);
    return (
      <div style={{ padding: "20px", fontFamily: "Inter, Arial, sans-serif" }}>
        <h1>Error Loading App</h1>
        <pre>{error instanceof Error ? error.message : String(error)}</pre>
      </div>
    );
  }
};

export default App;
